import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

/**
 * Minimal typed router over the History API. TanStack Router (spec §G) waits for the user's approval of
 * `isbot` (Unlicense); this module keeps the same shape (named routes, typed params) so the swap stays mechanical.
 */
export const ROUTES = {
  home: '/',
  login: '/login',
  signup: '/signup',
  reset: '/reset-password',
  callback: '/auth/callback',
  favorites: '/favorites',
  recent: '/recent',
  templates: '/templates',
  trash: '/trash',
  settings: '/settings',
  audit: '/settings/audit',
  settingsSection: '/settings/:section',
  search: '/search',
  project: '/p/:projectId',
  folder: '/p/:projectId/f/:folderId',
  board: '/b/:boardId',
  share: '/s',
  shared: '/shared',
  invite: '/invite',
  devUi: '/dev/ui',
} as const;

export type RouteName = keyof typeof ROUTES;
export type RouteMatch = { name: RouteName; params: Record<string, string>; query: URLSearchParams };

const PARAM = '([\\w-]{1,64})';
const COMPILED = (Object.entries(ROUTES) as [RouteName, string][]).map(([name, pattern]) => ({
  name,
  keys: [...pattern.matchAll(/:(\w+)/g)].map((m) => m[1]!),
  re: new RegExp(`^${pattern.replace(/:\w+/g, PARAM)}/?$`),
}));

/** The internal component gallery (/dev/ui) exists only in builds made with VITE_DEV_UI=1, never in production (P16-2). */
export function devUiEnabled(env: { readonly [key: string]: unknown }): boolean {
  return env.VITE_DEV_UI === '1';
}

const DEV_UI = devUiEnabled(import.meta.env);

export function matchRoute(path: string, search = '', { devUi = DEV_UI }: { devUi?: boolean } = {}): RouteMatch | null {
  for (const r of COMPILED) {
    if (r.name === 'devUi' && !devUi) continue;
    const m = r.re.exec(path);
    if (m) return { name: r.name, params: Object.fromEntries(r.keys.map((k, i) => [k, m[i + 1]!])), query: new URLSearchParams(search) };
  }
  return null;
}

export function href(name: RouteName, params: Record<string, string> = {}, query?: Record<string, string>): string {
  const path = ROUTES[name].replace(/:(\w+)/g, (_, k: string) => encodeURIComponent(params[k] ?? ''));
  const qs = query ? new URLSearchParams(query).toString() : '';
  return qs ? `${path}?${qs}` : path;
}

/** Only same-origin absolute paths are accepted as a post-login destination. */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return '/';
  return next;
}

const NAV_EVENT = 'os:navigate';

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(NAV_EVENT));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener('popstate', cb);
  window.addEventListener(NAV_EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(NAV_EVENT, cb);
  };
}

const snapshot = () => window.location.pathname + window.location.search;

/** Current path and query string, re-rendering on navigate() and on back/forward. */
export function useLocation(): { path: string; search: string } {
  const full = useSyncExternalStore(subscribe, snapshot, snapshot);
  const i = full.indexOf('?');
  return i < 0 ? { path: full, search: '' } : { path: full.slice(0, i), search: full.slice(i) };
}

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest} />;
}
